import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import LightBoxLayout from 'componets/light-box-layout';
import { CreateLicensePlateForm } from './create-license-plate-form';
import { EditTaxSettingForm } from 'modules/edit-tax-setting-form';
import { useEnterpriseLicensePlateTaxIdNumberUpdate } from '../use-license-pate-tax-id-number-update';
import analytics from 'service/analytics';

const STEPS = {
  CREATE_LICENSE_PLATE: 'create_license_plate',
  UPDATE_TAX_ID_NUMBER: 'update_tax_id_number',
};

const DEFAULT_STEP = STEPS.CREATE_LICENSE_PLATE;
const DEFAULT_LICENSE_PLATE = null;

export const CreateLicensePlateModal = ({ onClose, isOpen, enterpriseTaxSettings }) => {
  const queryClient = useQueryClient();

  const [step, setStep] = useState(DEFAULT_STEP);
  const [licensePlate, setCurrentLicensePlate] = useState(DEFAULT_LICENSE_PLATE);
  const [isTaxSettingUpdateLoading, setIsTaxSettingUpdateLoading] = useState(false);

  const reset = useCallback(() => {
    setStep(DEFAULT_STEP);
    setCurrentLicensePlate(DEFAULT_LICENSE_PLATE);
  }, []);

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const {
    updateEnterpriseLicensePlateTaxIdNumberMutateAsync,
    updateEnterpriseLicensePlateTaxIdNumberMutateError,
  } = useEnterpriseLicensePlateTaxIdNumberUpdate();

  const handleLicensePlateCreateSucceeded = useCallback(newLicensePlate => {
    setStep(STEPS.UPDATE_TAX_ID_NUMBER);
    setCurrentLicensePlate(newLicensePlate);
    analytics.licensePlatesCreateLicenseNextStepClick();
  }, []);

  const handleEditTaxSettingFormSubmit = useCallback(
    async taxSetting => {
      if (isTaxSettingUpdateLoading) return;

      analytics.licensePlatesCreateConfirmClick();

      setIsTaxSettingUpdateLoading(true);
      await updateEnterpriseLicensePlateTaxIdNumberMutateAsync({
        licensePlateId: licensePlate?.id,
        taxIdNumber: taxSetting.taxIdNumber,
      });

      await queryClient.invalidateQueries('license-plates');

      setIsTaxSettingUpdateLoading(false);

      handleClose();
    },
    [
      licensePlate,
      handleClose,
      queryClient,
      isTaxSettingUpdateLoading,
      updateEnterpriseLicensePlateTaxIdNumberMutateAsync,
    ]
  );

  let content = null;

  switch (step) {
    case STEPS.CREATE_LICENSE_PLATE:
      content = (
        <CreateLicensePlateForm
          onClose={handleClose}
          onLicensePlateCreateSucceeded={handleLicensePlateCreateSucceeded}
        />
      );
      break;
    case STEPS.UPDATE_TAX_ID_NUMBER:
      content = (
        <EditTaxSettingForm
          title="發票歸帳設定"
          description={`請選擇 ${licensePlate?.plateNumber} 發票歸帳設定。`}
          isLoading={isTaxSettingUpdateLoading}
          enterpriseTaxSettings={enterpriseTaxSettings}
          errorMessage={updateEnterpriseLicensePlateTaxIdNumberMutateError?.message}
          onSubmit={handleEditTaxSettingFormSubmit}
        />
      );
      break;

    default:
      throw new Error('Invalid step');
  }

  return <LightBoxLayout show={isOpen}>{content}</LightBoxLayout>;
};
